// SwallowAddonModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaMinus, FaPlus, FaTimes } from "react-icons/fa";
import styles from "./SwallowAddonModal.module.css";

// Lets a guest pick a required soup plus optional protein add-ons for a
// swallow-class menu item (e.g. Pounded Yam) before it's added to the cart,
// matching what the backend order endpoint requires/prices server-side.
const SwallowAddonModal = ({ isOpen, item, addons, onClose, onConfirm }) => {
  const [soupId, setSoupId] = useState("");
  const [proteinIds, setProteinIds] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState("");

  const soups = useMemo(() => addons.filter(a => a.type === "soup"), [addons]);
  const proteins = useMemo(() => addons.filter(a => a.type === "protein"), [addons]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSoupId("");
      setProteinIds([]);
      setQuantity(1);
      setError("");
    }
  }, [isOpen, item]);

  if (!isOpen || !item) return null;

  const toggleProtein = (addonId) => {
    setProteinIds(prev => (prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]));
  };

  const selectedSoup = soups.find(s => String(s.id) === String(soupId));

  // Every add-on has a single price now (no vendor tiers).
  const selectedProteinLines = proteins
    .filter(addon => proteinIds.includes(addon.id))
    .map(addon => ({ addon, price: parseFloat(addon.price) || 0 }));

  const addonUnitTotal =
    (selectedSoup ? parseFloat(selectedSoup.price) : 0) +
    selectedProteinLines.reduce((sum, line) => sum + line.price, 0);

  const unitTotal = parseFloat(item.base_price) + addonUnitTotal;
  const lineTotal = unitTotal * quantity;

  const handleConfirm = () => {
    if (!selectedSoup) {
      setError("Please choose a soup.");
      return;
    }

    const addonsPayload = [
      { addon_id: selectedSoup.id, name: selectedSoup.name, price: parseFloat(selectedSoup.price) },
      ...selectedProteinLines.map(line => ({
        addon_id: line.addon.id,
        name: line.addon.name,
        price: line.price
      }))
    ];

    onConfirm({
      id: item.id,
      name: item.name,
      price: unitTotal,
      quantity,
      addons: addonsPayload
    });
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.swallowModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{item.name}</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <p className={styles.modalSubtext}>Choose your soup and any extras.</p>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Soup <span className={styles.required}>required</span></h3>
          <select
            className={styles.select}
            value={soupId}
            onChange={e => { setSoupId(e.target.value); setError(""); }}
          >
            <option value="">Select a soup...</option>
            {soups.map(soup => (
              <option key={soup.id} value={soup.id}>
                {soup.name} — ₦{parseFloat(soup.price).toLocaleString()}
              </option>
            ))}
          </select>
        </div>

        {proteins.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Add-ons <span className={styles.optional}>optional</span></h3>
            <div className={styles.proteinList}>
              {proteins.map(addon => (
                <div key={addon.id} className={styles.proteinRow}>
                  <label className={styles.proteinLabel}>
                    <input
                      type="checkbox"
                      checked={proteinIds.includes(addon.id)}
                      onChange={() => toggleProtein(addon.id)}
                    />
                    {addon.name}
                  </label>
                  <span>+₦{(parseFloat(addon.price) || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.quantityRow}>
          <span>Quantity</span>
          <div className={styles.quantityControls}>
            <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}><FaMinus /></button>
            <span>{quantity}</span>
            <button type="button" onClick={() => setQuantity(q => q + 1)}><FaPlus /></button>
          </div>
        </div>

        {error && <p className={styles.formError}>{error}</p>}

        <button type="button" className={styles.confirmBtn} onClick={handleConfirm}>
          Add to Order — ₦{lineTotal.toLocaleString()}
        </button>
      </div>
    </div>
  );
};

export default SwallowAddonModal;
